import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
	Bank,
	Banner,
	DetailPayment,
	Doctor,
	DoctorSchedule,
	Faskes,
	Patient,
	Payment,
	Poli,
} from "~/types";
import type {
	PortalPatient,
	SelectedTenant,
} from "~/types/portal-patient.types";
import type { RoleType, UserRole } from "~/types/portal-roles.types";
import { apiFetcher } from "./utils.client";

type PortalState = {
	isAuthenticated: boolean;
	portalPatient: PortalPatient | null;
	branchPatient: Patient | null; // Patient data specific to selected branch
	selectedTenant: SelectedTenant | null;
	selectedPoli: Poli | null;
	authToken: string | null;
	tokenExpiry: number | null; // Unix timestamp
	isFetchingBranchPatient: boolean;
	// Multi-role support
	availableRoles: UserRole[];
	activeRole: UserRole | null;
	setAuthenticated: (isAuth: boolean) => void;
	setPortalPatient: (patient: PortalPatient | null) => void;
	setBranchPatient: (patient: Patient | null) => void;
	fetchBranchPatient: () => Promise<boolean>;
	setSelectedTenant: (tenant: SelectedTenant | null) => void;
	setSelectedPoli: (poli: Poli | null) => void;
	setAuthToken: (token: string | null, expiresIn?: number) => void;
	extendSession: (additionalSeconds?: number) => void;
	refreshToken: () => Promise<boolean>;
	isTokenExpired: () => boolean;
	logout: () => void;
	// Multi-role methods
	setAvailableRoles: (roles: UserRole[]) => void;
	switchRole: (roleType: RoleType) => void;
	getActiveRoleType: () => RoleType;
	// Portal booking flow helpers
	isPortalFlow: () => boolean;
	getTenantId: () => string | null;
	canStartBooking: () => boolean;
	clearTenantContext: () => void;
	clearBookingFlow: () => void;
	resetToMenu: () => void;
};

export const usePortalState = create<PortalState>()(
	persist(
		(set, get) => ({
			isAuthenticated: false,
			portalPatient: null,
			branchPatient: null,
			selectedTenant: null,
			selectedPoli: null,
			authToken: null,
			tokenExpiry: null,
			isFetchingBranchPatient: false,
			availableRoles: [],
			activeRole: null,
			setAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
			setPortalPatient: (portalPatient) => set({ portalPatient }),
			setBranchPatient: (branchPatient) => set({ branchPatient }),
			fetchBranchPatient: async () => {
				try {
					const {
						portalPatient,
						selectedTenant,
						authToken,
						isFetchingBranchPatient,
					} = get();

					// Prevent duplicate fetch
					if (isFetchingBranchPatient) {
						return false;
					}

					if (!portalPatient?.global_patient_id || !selectedTenant?.tenant_id) {
						return false;
					}

					// Mark as fetching
					set({ isFetchingBranchPatient: true });

					const response = await fetch("/api/portal/branch-patient-record", {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							"x-tenant-id": selectedTenant.tenant_id,
							...(authToken && { "portal-access-token": authToken }),
						},
						body: JSON.stringify({
							global_patient_id: portalPatient.global_patient_id,
						}),
					});

					if (!response.ok) {
						const errorData = await response.json().catch(() => ({}));
						return false;
					}

					const result = await response.json();

					if (result.results?.success && result.results?.results) {
						// Set branch patient data from nested results
						get().setBranchPatient(result.results.results);
						set({ isFetchingBranchPatient: false });
						return true;
					}

					// Also handle direct success format
					if (result.success && result.results) {
						// Set branch patient data
						get().setBranchPatient(result.results);
						set({ isFetchingBranchPatient: false });
						return true;
					}

					set({ isFetchingBranchPatient: false });
					return false;
				} catch (error) {
					set({ isFetchingBranchPatient: false });
					return false;
				}
			},
			setSelectedTenant: async (selectedTenant) => {
				set({ selectedTenant });

				// Auto-fetch branch patient data when tenant is selected
				if (selectedTenant && get().portalPatient?.global_patient_id) {
					const fetchSuccess = await get().fetchBranchPatient();
					if (!fetchSuccess) {
						console.warn("Failed to fetch branch patient data");
					}
				}
			},
			setSelectedPoli: (selectedPoli) => {
				set({ selectedPoli });
			},
			setAuthToken: (authToken, expiresIn = 3600) => {
				// Default 1 hour if not specified
				const tokenExpiry = authToken ? Date.now() + expiresIn * 1000 : null;
				set({ authToken, tokenExpiry });
			},
			// Temporary method to extend current session (until refresh endpoint is ready)
			extendSession: (additionalSeconds = 3600) => {
				const { tokenExpiry } = get();
				if (tokenExpiry) {
					const newExpiry =
						Math.max(Date.now(), tokenExpiry) + additionalSeconds * 1000;
					set({ tokenExpiry: newExpiry });
				}
			},
			isTokenExpired: () => {
				const { tokenExpiry } = get();
				if (!tokenExpiry) return true;
				return Date.now() >= tokenExpiry;
			},
			refreshToken: async () => {
				const { portalPatient, authToken, selectedTenant } = get();
				if (!portalPatient) return false;

				// Determine tenant ID based on context
				let tenantId = "portal";

				if (typeof window !== "undefined") {
					const pathname = window.location.pathname;
					const hostname = window.location.hostname;

					// Check if we're on homepage or branch list page (not in branch-specific flow)
					const isOnHomepage =
						pathname === "/" || pathname.startsWith("/portal-branches");

					if (isOnHomepage && hostname.includes("portal-")) {
						// Extract company code from hostname (portal-{companycode}.domain)
						const alias = hostname.split(".")[0]; // "portal-tm"
						const companyCode = alias.replace("portal-", ""); // "tm"
						tenantId = companyCode;
					} else if (selectedTenant?.tenant_id) {
						// Use selected tenant if in branch-specific flow
						tenantId = selectedTenant.tenant_id;
					} else if (hostname.includes("portal-")) {
						// Fallback: extract from hostname
						const alias = hostname.split(".")[0];
						const companyCode = alias.replace("portal-", "");
						tenantId = companyCode;
					}
				}

				try {
					// Call refresh endpoint
					const response = await fetch("/api/portal/auth/refresh", {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							"x-tenant-id": tenantId,
							...(authToken && { "portal-access-token": authToken }),
						},
						body: JSON.stringify({
							global_patient_id: portalPatient.global_patient_id,
						}),
					});

					if (!response.ok) {
						return false;
					}

					const result = await response.json();
					const refreshData = result.results || result;

					if (refreshData.success && refreshData.data?.auth_token) {
						// Update token with new expiry (default 1 hour if not specified)
						get().setAuthToken(
							refreshData.data.auth_token,
							refreshData.data.expires_in || 3600,
						);
						return true;
					}
					return false;
				} catch (error) {
					return false;
				}
			},
			setAvailableRoles: (roles) => {
				set({ availableRoles: roles });
				const { activeRole } = get();
				if (!activeRole && roles.length > 0) {
					set({ activeRole: roles[0] });
				}
			},
			switchRole: (roleType) => {
				const { availableRoles } = get();
				const role = availableRoles.find((r) => r.role_type === roleType);
				if (role) {
					set({ activeRole: role });
				}
			},
			getActiveRoleType: () => {
				const { activeRole } = get();
				return activeRole?.role_type || "pasien";
			},
			logout: () => {
				set({
					isAuthenticated: false,
					portalPatient: null,
					branchPatient: null,
					selectedTenant: null,
					selectedPoli: null,
					authToken: null,
					tokenExpiry: null,
					availableRoles: [],
					activeRole: null,
				});
			},
			// Portal booking flow helpers
			isPortalFlow: () => {
				const { isAuthenticated, selectedTenant } = get();
				return isAuthenticated && selectedTenant !== null;
			},
			getTenantId: () => {
				const { selectedTenant } = get();
				const tenantId = selectedTenant?.tenant_id || null;
				return tenantId;
			},
			canStartBooking: () => {
				const { isAuthenticated, selectedTenant } = get();
				return (
					isAuthenticated &&
					selectedTenant !== null &&
					selectedTenant.operational_status === "open"
				);
			},
			clearTenantContext: () => {
				set({ selectedTenant: null });
			},
			clearBookingFlow: () => {
				set({ selectedPoli: null });
			},
			// Reset all branch-specific state when returning to menu
			resetToMenu: () => {
				set({
					selectedTenant: null,
					selectedPoli: null,
					branchPatient: null,
				});
				// Clear localStorage backup as well
				try {
					localStorage.removeItem("manual-portal-tenant");
				} catch (e) {
					console.error("Error removing manual backup:", e);
				}
			},
		}),
		{
			name: "portal-auth-storage",
			// Enable hydration for proper persistence
			partialize: (state) => ({
				isAuthenticated: state.isAuthenticated,
				portalPatient: state.portalPatient,
				branchPatient: state.branchPatient,
				selectedTenant: state.selectedTenant,
				selectedPoli: state.selectedPoli,
				authToken: state.authToken,
				tokenExpiry: state.tokenExpiry,
				availableRoles: state.availableRoles,
				activeRole: state.activeRole,
			}),
			version: 3,
			migrate: (persistedState: unknown, version) => {
				const state = persistedState as Record<string, unknown>;
				if (version < 2) {
					state.branchPatient = null;
				}
				if (version < 3) {
					state.availableRoles = [];
					state.activeRole = null;
				}
				return state;
			},
			onRehydrateStorage: () => {
				return (state, error) => {
					if (error) {
						console.error("Zustand rehydration error:", error);
					}
				};
			},
		},
	),
);

type AppState = {
	faskes: Faskes | null;
	patient: Patient | null;
	banners: Banner[] | null;
	csrf: string | null;
	setFaskes: (faskes: Faskes) => void;
	setBanner: (banner: Banner[]) => void;
	setCsrf: (csrf: string) => void;
	setPatient: (patient: Patient) => void;
};
export const useAppState = create<AppState>((set) => ({
	faskes: null,
	patient: null,
	banners: null,
	csrf: null,
	setFaskes: (faskes: Faskes) => set({ faskes }),
	setBanner: (banners: Banner[]) => set({ banners }),
	setCsrf: (csrf) => set({ csrf }),
	setPatient: (patient: Patient) => set({ patient }),
}));

type BookingState = {
	doctor: Doctor | null;
	setDoctor: (doctor: Doctor | null) => void;
	schedule: DoctorSchedule | null;
	setSchedule: (schedule: DoctorSchedule | null) => void;
	payment: Payment | null;
	setPayment: (payment: Payment | null) => void;
	poli: Poli | null;
	setPoli: (poli: Poli | null) => void;
	bank: Bank | null;
	setBank: (bank: Bank | null) => void;
	detailPayment: DetailPayment | null;
	setDetailPayment: (detailPayment: DetailPayment | null) => void;
	clearBookingState: () => void;
};

export const useBookingState = create<BookingState>()(
	persist(
		(set) => ({
			doctor: null,
			schedule: null,
			payment: null,
			poli: null,
			bank: null,
			detailPayment: null,
			setPayment: (payment) => {
				console.log("💾 Saving payment to localStorage:", payment);
				set({ payment });
			},
			setBank: (bank) => {
				console.log("💾 Saving bank to localStorage:", bank);
				set({ bank });
			},
			setDoctor: (doctor) => {
				console.log("💾 Saving doctor to localStorage:", doctor);
				set({ doctor });
			},
			setSchedule: (schedule) => {
				console.log("💾 Saving schedule to localStorage:", schedule);
				set({ schedule });
			},
			setPoli: (poli) => {
				console.log("💾 Saving poli to localStorage:", poli);
				set({ poli });
			},
			setDetailPayment: (detailPayment) => {
				console.log("💾 Saving detailPayment to localStorage:", detailPayment);
				set({ detailPayment });
			},
			clearBookingState: () =>
				set({
					doctor: null,
					schedule: null,
					payment: null,
					poli: null,
					bank: null,
					detailPayment: null,
				}),
		}),
		{
			name: "booking-state-storage",
			// Persist doctor, payment, schedule, poli for booking flow
			partialize: (state) => ({
				doctor: state.doctor,
				payment: state.payment,
				schedule: state.schedule,
				poli: state.poli,
				bank: state.bank,
				detailPayment: state.detailPayment,
			}),
		},
	),
);
